'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { auth } from '@/lib/api'
import { useSettingsStore } from '@/store/settings-store'
import { Spinner } from '@/components/ui/cli-spinner'

/**
 * Guards all routes behind authentication.
 * Validates session on each navigation. Retries once before redirecting
 * to /login — prevents logout on transient network hiccups.
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const [ready, setReady] = useState(false)
  const { setUserId, setFullName } = useSettingsStore()

  useEffect(() => {
    setReady(false)
    let cancelled = false

    const validate = async () => {
      // Try auth — retry once on failure (handles transient errors)
      let user = await auth.me()
      if (!user) {
        await new Promise(r => setTimeout(r, 500))
        user = await auth.me()
      }

      if (cancelled) return

      // Keep store in sync with auth state
      if (user) {
        setUserId(user.user_id)
        setFullName(user.full_name || '')
      }

      if (pathname.startsWith('/login')) {
        if (user) {
          router.replace('/')
        } else {
          setReady(true)
        }
      } else {
        if (user) {
          setReady(true)
        } else {
          router.replace('/login')
        }
      }
    }

    validate()
    return () => { cancelled = true }
  }, [pathname, router, setUserId, setFullName])

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-5">
          <img src="/kael-logo.svg" alt="Kael" className="h-12 w-12 opacity-80" />
          <div className="flex items-center gap-3 text-muted-foreground">
            <Spinner name="breathe" size="1.2rem" color="var(--brand)" />
            <span className="text-sm font-medium">Authenticating</span>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
